/**
 * PermissionGuard Component
 * 
 * Wrapper component that conditionally renders children based on permissions
 * Supports multiple check types and optional fallback content
 * 
 * @example
 * ```tsx
 * // Show button only if user can create transactions
 * <PermissionGuard check="canCreate" module="transactions">
 *   <Button>+ Novo Lançamento</Button>
 * </PermissionGuard>
 * 
 * // Show delete button only if user can delete
 * <PermissionGuard check="canDelete" module="budgets">
 *   <Button variant="destructive">Deletar</Button>
 * </PermissionGuard>
 * 
 * // Show page only if user can access it
 * <PermissionGuard check="canAccess" page="sharing">
 *   <SharingPage />
 * </PermissionGuard>
 * 
 * // With fallback content
 * <PermissionGuard 
 *   check="canEdit" 
 *   module="transactions"
 *   fallback={<Text>Você não tem permissão para editar</Text>}
 * >
 *   <EditForm />
 * </PermissionGuard>
 * ```
 */

import { ReactNode } from 'react';
import { usePermissions } from '../../hooks/usePermissions';

type Module = 'transactions' | 'budgets' | 'reports' | 'scenarios';
type RestrictedPage = 'settings' | 'sharing' | 'configurations';

interface PermissionGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
  check: 'canCreate' | 'canEdit' | 'canDelete' | 'canAccess' | 'canManageMembers' | 'canInviteUsers' | 'canEditSettings' | 'canSaveFilters';
  module?: Module;
  page?: RestrictedPage;
}

/**
 * Guard component that shows/hides content based on permissions
 */
export function PermissionGuard({ 
  children, 
  fallback = null, 
  check,
  module,
  page,
}: PermissionGuardProps) {
  const permissions = usePermissions();

  // If still loading permissions, don't render anything
  if (permissions.loading) {
    return null;
  }

  let hasPermission = false;

  // Check permission based on check type
  switch (check) {
    case 'canCreate':
      hasPermission = module ? permissions.canCreate(module) : false;
      break;
    case 'canEdit':
      hasPermission = module ? permissions.canEdit(module) : false;
      break;
    case 'canDelete':
      hasPermission = module ? permissions.canDelete(module) : false;
      break;
    case 'canAccess':
      hasPermission = page ? permissions.canAccess(page) : false;
      break;
    case 'canManageMembers':
      hasPermission = permissions.canManageMembers;
      break;
    case 'canInviteUsers':
      hasPermission = permissions.canInviteUsers;
      break;
    case 'canEditSettings':
      hasPermission = permissions.canEditSettings;
      break;
    case 'canSaveFilters':
      hasPermission = permissions.canSaveFilters;
      break;
    default:
      hasPermission = false;
  }

  // Show children if has permission, otherwise show fallback
  return hasPermission ? <>{children}</> : <>{fallback}</>;
}

